import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { listWorkOrders, updateWorkOrder, listSites } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { StatusPill, TypePill, PriorityPill } from "@/components/Pills";
import { Hammer, PlayCircle, CheckCircle2, PauseCircle, ChevronRight, Navigation } from "lucide-react";
import { toast } from "sonner";

/** Field Mode: a touch-friendly job execution screen for technicians. */
export default function Field() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [siteMap, setSiteMap] = useState({});

  const load = async () => {
    const [data, sites] = await Promise.all([listWorkOrders(), listSites()]);
    setSiteMap(Object.fromEntries(sites.map((s) => [s.id, s])));
    setJobs(
      data.filter((w) => ["open", "in_progress", "on_hold"].includes(w.status))
    );
  };
  useEffect(() => {
    load();
  }, []);

  const advance = async (id, status) => {
    await updateWorkOrder(id, { status });
    toast.success(`→ ${status}`);
    load();
  };

  return (
    <Layout title="Field Mode" breadcrumbs={["Mobile", "Field Execution"]}>
      <div className="bg-zinc-950 text-white -mx-4 md:-mx-6 -mt-4 md:-mt-6 px-4 md:px-6 py-6 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-sm bg-orange-500 flex items-center justify-center">
            <Hammer className="h-6 w-6 text-zinc-950" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-orange-400">
              On Duty
            </div>
            <div className="font-display font-extrabold text-2xl">
              {user?.name}
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="font-mono text-2xl font-bold">{jobs.length}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">
              Active Jobs
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {jobs.map((w) => (
          <div
            key={w.id}
            data-testid={`field-job-${w.number}`}
            className="bg-white border border-zinc-200 rounded-sm overflow-hidden"
          >
            <div className="p-4 border-b border-zinc-100">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm font-bold text-zinc-950">{w.number}</span>
                <StatusPill status={w.status} />
              </div>
              <Link
                to={`/work-orders/${w.id}`}
                className="block font-display font-bold text-lg text-zinc-950 hover:text-orange-600"
              >
                {w.title}
              </Link>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <TypePill type={w.type} />
                <PriorityPill priority={w.priority} />
                {w.due_date && (
                  <span className="text-xs font-mono text-zinc-500">
                    due {new Date(w.due_date).toLocaleDateString()}
                  </span>
                )}
                {siteMap[w.site_id]?.map_url && (
                  <a
                    data-testid={`field-map-${w.number}`}
                    href={siteMap[w.site_id].map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-orange-300 bg-orange-50 hover:bg-orange-100 text-[10px] font-bold uppercase tracking-wider text-orange-800"
                  >
                    <Navigation className="h-3 w-3" /> Navigate
                  </a>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 divide-x divide-zinc-100">
              {w.status === "open" && (
                <button
                  onClick={() => advance(w.id, "in_progress")}
                  className="py-4 flex flex-col items-center gap-1 text-orange-700 font-bold text-xs uppercase tracking-wider hover:bg-orange-50 col-span-2"
                  data-testid={`field-start-${w.number}`}
                >
                  <PlayCircle className="h-5 w-5" /> Start Job
                </button>
              )}
              {w.status === "in_progress" && (
                <>
                  <button
                    onClick={() => advance(w.id, "on_hold")}
                    className="py-4 flex flex-col items-center gap-1 text-yellow-800 font-bold text-xs uppercase tracking-wider hover:bg-yellow-50"
                    data-testid={`field-hold-${w.number}`}
                  >
                    <PauseCircle className="h-5 w-5" /> Hold
                  </button>
                  <button
                    onClick={() => advance(w.id, "completed")}
                    className="py-4 flex flex-col items-center gap-1 text-emerald-800 font-bold text-xs uppercase tracking-wider hover:bg-emerald-50"
                    data-testid={`field-done-${w.number}`}
                  >
                    <CheckCircle2 className="h-5 w-5" /> Complete
                  </button>
                </>
              )}
              {w.status === "on_hold" && (
                <button
                  onClick={() => advance(w.id, "in_progress")}
                  className="py-4 flex flex-col items-center gap-1 text-orange-700 font-bold text-xs uppercase tracking-wider hover:bg-orange-50 col-span-2"
                  data-testid={`field-resume-${w.number}`}
                >
                  <PlayCircle className="h-5 w-5" /> Resume
                </button>
              )}
              <Link
                to={`/work-orders/${w.id}`}
                className="py-4 flex flex-col items-center gap-1 text-zinc-700 font-bold text-xs uppercase tracking-wider hover:bg-zinc-50 ml-auto"
                data-testid={`field-open-${w.number}`}
              >
                <ChevronRight className="h-5 w-5" /> Open
              </Link>
            </div>
          </div>
        ))}
        {jobs.length === 0 && (
          <div className="text-center py-12 text-sm text-zinc-500">
            No active jobs. You&apos;re caught up.
          </div>
        )}
      </div>
    </Layout>
  );
}
